import { Drawer } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React from 'react';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { useThemeStore } from '~/components/theme-controller/stores/useThemeStore';
import { isDarkTheme } from '~/components/theme-controller/utils/theme-utils/theme-utils';
import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useBrandsAndProducts } from '~/react-query-hooks/knowledge-graph/useBrandsAndProducts';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useBrandAndProductDrawer } from '~/widgets/knowledge-graph-widget/components/brands-and-products-tab/useBrandAndProductDrawer';

import * as S from './BrandAndProductDrawer.styles';
import { BrandAndProductDrawerContent } from './components/BrandAndProductDrawerContent';

export interface BrandAndProductDrawerProps {
  entity: Entity;
}

export const BrandAndProductDrawer = ({
  entity,
}: BrandAndProductDrawerProps) => {
  const { closeDrawer, isDrawerOpen, product } = useBrandAndProductDrawer();
  const { data, error } = useBrandsAndProducts(entity.tag);
  const themeName = useThemeStore(state => state.themeName);

  if (error) {
    throw error;
  }

  const productAndBrand = data?.find(item => item.product === product);
  const isDark = isDarkTheme(themeName);
  const logoUrl = isDark
    ? entity.logo_urls.light_png
    : entity.logo_urls.dark_png;

  if (!productAndBrand) {
    return null;
  }

  return (
    <Drawer
      data-testid="brand-product-exposure-drawer"
      isOpen={isDrawerOpen}
      handleCloseClick={closeDrawer}
      headerButton={<S.HeaderTitle>{productAndBrand.product}</S.HeaderTitle>}
    >
      <ErrorBoundaryWrapper>
        <WithFooterWrapper
          isWrappedComponentOpened={isDrawerOpen}
          trackingMetadata={{
            source: POSTHOG_TRACKING_SOURCES.BRAND_AND_PRODUCT_DRAWER,
          }}
        >
          <BrandAndProductDrawerContent
            entity={entity}
            logoUrl={logoUrl}
            productAndBrand={productAndBrand}
          />
        </WithFooterWrapper>
      </ErrorBoundaryWrapper>
    </Drawer>
  );
};
